import { useCallback, useMemo, useState } from 'react';

import { GridSortModel } from '@mui/x-data-grid-premium';

import { capSortModel, removeSortLevel } from '../sortModel';

export interface UseTableSortingParams {
  groupByField?: string | null;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

export interface UseTableSortingReturn {
  effectiveSortModel: GridSortModel;
  effectiveOnSortModelChange: (model: GridSortModel) => void;
}

const useTableSorting = ({
  groupByField,
  sortModel,
  onSortModelChange,
}: UseTableSortingParams): UseTableSortingReturn => {
  const [inGroupSortModel, setInGroupSortModel] = useState<GridSortModel>(() =>
    groupByField ? capSortModel(sortModel) : [],
  );
  const [seededForGroupByField, setSeededForGroupByField] = useState(groupByField);

  if (groupByField !== seededForGroupByField) {
    setSeededForGroupByField(groupByField);
    setInGroupSortModel(groupByField ? capSortModel(sortModel) : []);
  }

  const effectiveSortModel = useMemo<GridSortModel>(
    () => (groupByField ? inGroupSortModel : capSortModel(sortModel)),
    [groupByField, inGroupSortModel, sortModel],
  );

  const effectiveOnSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const capped = capSortModel(newModel);
      if (!groupByField) {
        onSortModelChange?.(capped);
        return;
      }
      setInGroupSortModel(capped);
      // Report the in-group levels so the owner persists them (a remount re-seeds
      // from sortModel), but keep the stored group-field level as is: client-side
      // it never reorders the groups, and a server-sorted page would.
      const storedGroupLevel = sortModel?.find((item) => item.field === groupByField);
      const inGroupLevels = removeSortLevel(capped, groupByField);
      onSortModelChange?.(
        capSortModel(storedGroupLevel ? [storedGroupLevel, ...inGroupLevels] : inGroupLevels),
      );
    },
    [groupByField, sortModel, onSortModelChange],
  );

  return {
    effectiveSortModel,
    effectiveOnSortModelChange,
  };
};

export default useTableSorting;
